import { useCallback, useEffect, useRef, useState } from "react";
import { waitForPlacesLibrary } from "@/lib/googleMaps";

// Shared Google Places plumbing for every place-search box in the app — the
// map's own search bar and the facility address field both run on this.
//
// Two tiers, deliberately:
//   • Suggestions come from the Places library, which loads as its own async
//     script alongside main.js and can also answer REQUEST_DENIED if Places
//     API isn't enabled on the key.
//   • Resolving a pick, and plain Enter-to-search, go through
//     google.maps.Geocoder — core API, always present.
// So a search box always works even if `places` never lands; the dropdown is
// the enhancement. One place owns these quirks so a fix lands everywhere.

const DEBOUNCE_MS = 220;
const MAX_SUGGESTIONS = 6;
const MIN_QUERY_LENGTH = 3;

export function usePlaceSearch({ maps, query, bias }) {
  const [suggestions, setSuggestions] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [placesReady, setPlacesReady] = useState(false);

  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  // Monotonic request id: autocomplete replies out of order often enough that
  // a slow response for "dal" would otherwise overwrite "dallas tx".
  const seqRef = useRef(0);
  // Set by suppressNext() when the caller writes to `query` itself. Without
  // it, filling the input with a chosen place's name looks exactly like
  // typing it and the dropdown reopens one debounce later.
  const skipRef = useRef(false);
  // `bias` may be a live getter (the map's current bounds), so it is read at
  // request time rather than captured as an effect dependency.
  const biasRef = useRef(bias);
  useEffect(() => { biasRef.current = bias; }, [bias]);

  useEffect(() => {
    if (maps && !geocoderRef.current) geocoderRef.current = new maps.Geocoder();
  }, [maps]);

  useEffect(() => {
    if (!maps) return;
    let cancelled = false;
    waitForPlacesLibrary().then((places) => {
      if (cancelled || !places) return;
      autocompleteRef.current = new places.AutocompleteService();
      // Session tokens group the keystrokes of one search into a single
      // billable autocomplete session instead of one per character.
      sessionTokenRef.current = new places.AutocompleteSessionToken();
      // State, not just a ref, so a fast typer whose 3rd keystroke landed
      // before places.js did still gets suggestions.
      setPlacesReady(true);
    });
    return () => { cancelled = true; };
  }, [maps]);

  const suppressNext = useCallback(() => { skipRef.current = true; }, []);
  const clearSuggestions = useCallback(() => setSuggestions([]), []);

  useEffect(() => {
    const q = (query || "").trim();
    clearTimeout(debounceRef.current);
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    if (q.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      return;
    }
    const service = autocompleteRef.current;
    if (!service) return; // Places unavailable — Enter-to-geocode still works

    debounceRef.current = setTimeout(() => {
      const seq = ++seqRef.current;
      const request = { input: q, sessionToken: sessionTokenRef.current };
      const bounds = typeof biasRef.current === "function" ? biasRef.current() : biasRef.current;
      // Bias (not restrict) toward the given area, so "Main St" resolves near
      // the fleet while a search for a distant city still works.
      if (bounds) request.bounds = bounds;

      service.getPlacePredictions(request, (predictions, status) => {
        if (seq !== seqRef.current) return; // a newer keystroke already won
        if (status !== maps.places.PlacesServiceStatus.OK || !predictions?.length) {
          setSuggestions([]);
          return;
        }
        setSuggestions(
          predictions.slice(0, MAX_SUGGESTIONS).map((p) => ({
            placeId: p.place_id,
            primary: p.structured_formatting?.main_text || p.description,
            secondary: p.structured_formatting?.secondary_text || "",
            description: p.description,
          }))
        );
      });
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query, maps, placesReady]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  /**
   * Resolve a `{ placeId }` or `{ address }` request to a real location.
   * Rejects with a message already fit to show a user.
   */
  const resolve = useCallback((request) => {
    const geocoder = geocoderRef.current;
    if (!geocoder) return Promise.reject(new Error("Search is unavailable right now"));
    setResolving(true);
    return new Promise((resolvePromise, reject) => {
      geocoder.geocode(request, (results, status) => {
        setResolving(false);
        if (status !== "OK" || !results?.length) {
          reject(new Error(
            status === "ZERO_RESULTS"
              ? "No match for that place"
              : "Search is unavailable right now"
          ));
          return;
        }
        const hit = results[0];
        resolvePromise({
          lat: hit.geometry.location.lat(),
          lng: hit.geometry.location.lng(),
          address: hit.formatted_address,
          location: hit.geometry.location,
          viewport: hit.geometry.viewport || null,
        });
      });
    });
  }, []);

  return { suggestions, resolve, resolving, suppressNext, clearSuggestions };
}
